import { ChatSlaConf } from '@agent/agent-interface/contact-view/chat/model/chat-sla-conf';
import { MessageType } from '@agent/agent-interface/contact-view/chat/model/message-type';
import { ScreenSharingInfo } from '@agent/agent-interface/contact-view/chat/model/screen-sharing-info';

import { Booking } from '@contact/upgrade';

import { ChatMessage } from './chat-message';
import { ChatSystemMessage } from './chat-system-message';
import { ChatTab } from './chat-tab';

export class ChatModel {
  public customerId: string;
  public messages: Array<ChatMessage> = [];
  public systemMessages: ChatSystemMessage[] = [];
  public typing: ChatSystemMessage[] = [];
  public typingCounterCustomer: number = 0;
  public typingCounterAgent: number = 0;
  public tab: ChatTab;
  public isNew: boolean = true;
  public chatSlaConf: ChatSlaConf | undefined;
  public inTransferMode: boolean = false;
  public inConferenceMode: boolean = false;
  public inPostTreatmentMode: boolean = false;
  public inPostTreatmentExceededMode: boolean = false;
  public chatTabState: string = 'chat';
  public conference: boolean = false;
  public conferenceCsrName: string;
  public chatAttachmentCount: number = 0;
  public screenSharingInfos: ScreenSharingInfo = new ScreenSharingInfo();
  public checkCapabilityForScreenSharing: boolean = true;
  private GROUPED_MESSAGE_INTERVAL = 300000;
  public owiSextId: string;
  public responseId: string;
  public connectionId: string;
  public requestingCsrNickname: string;
  public transferred: boolean = false;
  public customerCardId: string | undefined;
  public inactiveTimer: any = null;
  public isCustomerInactive = false;

  constructor(customerId: string, contact: Booking, messages: Array<ChatMessage>, chatSlaConf?: ChatSlaConf) {
    this.responseId = contact.responseId;
    this.connectionId = contact.connectionId;
    this.customerId = customerId;
    this.transferred = !!contact.forward;
    this.customerCardId = contact.customerId;
    if (messages) {
      messages.forEach(message => this.groupMessagesIfNeeded(message));
    }
    this.chatSlaConf = chatSlaConf;
    this.tab = ChatTab.fromChat(this);
    if (contact.forward) {
      this.conferenceCsrName = (contact.forward && contact.forward.agentName) || '';
      this.conference = contact.conference;
      this.inConferenceMode = true;
      this.requestingCsrNickname = (contact.forward && contact.forward.agentName) || '';
    }
  }

  public addMessage(msg: ChatMessage) {
    if (msg.type === MessageType.FILE) {
      this.chatAttachmentCount++;
    }
    this.groupMessagesIfNeeded(msg);
    this.tab.updateChatTabInfo(msg);
  }

  public increaseUnreadCount() {
    this.tab.increaseUnreadCount();
  }

  public resetUnreadCount() {
    this.tab.resetUnreadCount();
  }

  public timeBeforeReminderExceeded(): boolean {
    return (
      !!this.chatSlaConf &&
      this.tab.timerForLastMessage >= this.chatSlaConf.slaDuration - this.chatSlaConf.slaReminder &&
      this.tab.timerForLastMessage < this.chatSlaConf.slaDuration
    );
  }

  public durationExceeded(): boolean {
    return !!this.chatSlaConf && this.tab.timerForLastMessage >= this.chatSlaConf.slaDuration;
  }

  public removeMessagesOfType(type: MessageType) {
    let index;
    do {
      index = this.messages.findIndex(val => val.type === type);
      if (index > -1) {
        this.messages.splice(index, 1);
      }
    } while (index > -1);
  }

  private groupMessagesIfNeeded(msg: ChatMessage) {
    const filterMessages = this.messages.filter(message => message.type !== MessageType.JOIN);
    const lastMessage = filterMessages[filterMessages.length - 1];
    if (
      lastMessage &&
      !lastMessage.chatbotExchangeMessage &&
      !msg.chatbotExchangeMessage &&
      lastMessage.type === MessageType.CHAT &&
      msg.type === MessageType.CHAT &&
      ((lastMessage.isFromCustomer() && msg.isFromCustomer()) ||
        (!lastMessage.isFromCustomer() && !msg.isFromCustomer() && lastMessage.csrId === msg.csrId)) &&
      msg.date - lastMessage.date < this.GROUPED_MESSAGE_INTERVAL
    ) {
      if (lastMessage.content) {
        lastMessage.content += '\n' + msg.content;
      } else {
        lastMessage.content = msg.content;
      }
    } else {
      this.messages.push(msg);
    }
  }

  /**
   * Get the date of the last message (fisrt message in the last group message) if the message comes from the customer.
   * The date will be use to add time of the SLA timer and last message timer.
   * returns null if last customer message has an answer
   */
  public getOldestUnansweredMessageFromCustomer(): ChatMessage | undefined {
    let messageFromCustomer: ChatMessage | undefined = undefined;
    const reverseChatMessages = this.messages.filter(message => message.type === MessageType.CHAT).reverse();
    for (let i = 0; i < reverseChatMessages.length; i++) {
      if (reverseChatMessages[i].isFromCustomer()) {
        messageFromCustomer = reverseChatMessages[i];
      } else {
        break;
      }
    }
    return messageFromCustomer;
  }
}
